/**
 * Answers the Form.io renderer's token refresh requests (SE-9160).
 *
 * The refresh re-runs `PlayerIntakeGetFormUrl` with the page's own variables
 * through `client.query` with `fetchPolicy: "no-cache"`: it goes to the
 * network like the page's `network-only` query, but writes nothing to the
 * cache that query watches. The hook's `refetch` — or any cache write — would
 * change the `url` the form was opened with, reload the iframe/WebView and
 * destroy what the scout has typed, which is the outcome this exists to
 * prevent.
 * @module features/player-intake/hooks/useFormTokenRefresh
 */

import { useApolloClient } from "@apollo/client";
import { type RefObject, useCallback, useLayoutEffect, useRef } from "react";

import type { WebView } from "react-native-webview";

import {
  PlayerIntakeGetFormUrlDocument,
  type PlayerIntakeGetFormUrlQuery,
  type PlayerIntakeGetFormUrlQueryVariables,
} from "@/generated/graphql";

import { originOf } from "../utils/originOf";
import {
  buildNativeReplyScript,
  buildTokenRefreshReply,
  TOKEN_REFRESH_FAILED_TYPE,
  type TokenRefreshReply,
} from "../utils/tokenRefresh";

const LOG_PREFIX = "[ScoutReport:Intake]";

/** Inputs for {@link useFormTokenRefresh}. */
interface UseFormTokenRefreshOptions {
  /** The variables the page's form URL query ran with. */
  readonly variables: PlayerIntakeGetFormUrlQueryVariables;
  /** The web form iframe. */
  readonly iframeRef: RefObject<HTMLIFrameElement | null>;
  /** The native form WebView. */
  readonly webViewRef: RefObject<WebView | null>;
}

/** A refresh query in flight, and the variables it ran with. */
interface InFlightRefresh {
  /** `JSON.stringify` of the query variables. */
  readonly key: string;
  /** Resolves to the fresh form URL, or null when there is none. */
  readonly promise: Promise<string | null>;
}

/**
 * Refresh-request handlers for the intake form host.
 * @param options - The query variables and the form refs.
 * @param options.variables - The variables the page's form URL query ran with.
 * @param options.iframeRef - The web form iframe.
 * @param options.webViewRef - The native form WebView.
 * @returns `answerOnWeb` / `answerOnNative`, each taking the request's id.
 */
export const useFormTokenRefresh = ({
  variables,
  iframeRef,
  webViewRef,
}: UseFormTokenRefreshOptions) => {
  const client = useApolloClient();
  // The one refresh query in flight, shared by every request that arrives
  // before it settles, so a burst of requests mints one token, not many. It is
  // keyed by the variables it ran with: a refresh started for a previous player
  // or match must never be handed to the form now open.
  const inFlightRef = useRef<InFlightRefresh | null>(null);
  // The variables key as of the most recent render. A reply is only ever
  // posted if this still matches the key captured when its request arrived —
  // otherwise the route moved to another player/match while the query was in
  // flight, iframeRef/webViewRef now point at a different form, and posting
  // would hand that form a credential minted for the one before it.
  const latestKeyRef = useRef(JSON.stringify(variables));

  const fetchFreshFormUrl = useCallback((): Promise<string | null> => {
    const key = JSON.stringify(variables);
    if (inFlightRef.current?.key === key) return inFlightRef.current.promise;
    const slot: InFlightRefresh = {
      key,
      promise: client
        .query<
          PlayerIntakeGetFormUrlQuery,
          PlayerIntakeGetFormUrlQueryVariables
        >({
          query: PlayerIntakeGetFormUrlDocument,
          variables,
          fetchPolicy: "no-cache",
        })
        .then(
          result => result.data?.getIntakeFormUrl ?? null,
          (error: unknown) => {
            console.warn(
              LOG_PREFIX,
              "Token refresh query failed:",
              error instanceof Error ? error.message : error
            );
            return null;
          }
        )
        .finally(() => {
          // Only free the slot if a newer refresh has not taken it.
          if (inFlightRef.current === slot) inFlightRef.current = null;
        }),
    };
    inFlightRef.current = slot;
    return slot.promise;
  }, [client, variables]);

  const fetchReply = useCallback(
    async (requestId: string): Promise<TokenRefreshReply> => {
      const reply = buildTokenRefreshReply(
        requestId,
        await fetchFreshFormUrl()
      );
      if (reply.type === TOKEN_REFRESH_FAILED_TYPE) {
        console.warn(LOG_PREFIX, "Token refresh returned no usable token");
      }
      return reply;
    },
    [fetchFreshFormUrl]
  );

  /*
   * Web: post to the iframe at its exact origin, read when the reply is sent.
   * Never "*" — the reply carries a credential, and a wildcard would hand it
   * to whatever page the frame has navigated to.
   */
  const answerOnWeb = useCallback(
    async (requestId: string) => {
      const requestKey = latestKeyRef.current;
      const reply = await fetchReply(requestId);
      if (latestKeyRef.current !== requestKey) return;
      const iframe = iframeRef.current;
      const targetOrigin = iframe?.src ? originOf(iframe.src) : null;
      if (!iframe?.contentWindow || targetOrigin === null) return;
      iframe.contentWindow.postMessage(JSON.stringify(reply), targetOrigin);
    },
    [fetchReply, iframeRef]
  );

  /*
   * Native: dispatch the reply from inside the renderer's own top frame,
   * targeted at the form origin so a navigated-away frame never receives it.
   */
  const answerOnNative = useCallback(
    async (requestId: string, targetOrigin: string) => {
      const requestKey = latestKeyRef.current;
      const reply = await fetchReply(requestId);
      if (latestKeyRef.current !== requestKey) return;
      webViewRef.current?.injectJavaScript(
        buildNativeReplyScript(reply, targetOrigin)
      );
    },
    [fetchReply, webViewRef]
  );

  useLayoutEffect(() => {
    latestKeyRef.current = JSON.stringify(variables);
  }, [variables]);

  return { answerOnWeb, answerOnNative };
};
